import { apiClient } from '@kss/api';
import type { SearchableSelectOption } from '@kss/ui';

/**
 * Shared async option-loaders for hand-built CRUD/placeholder forms that
 * need a foreign-key dropdown (SearchableSelect `loadOptions`).
 *
 * §35.3 note: FK columns store the parent's NUMERIC `id`, while `code`
 * is the display PK. So the option VALUE must be `row.id` (fall back to
 * `code` only when no numeric id is present, e.g. uniqueidentifier PKs).
 * The LABEL prefers `name`, then a firstName+lastName combo (Employee),
 * then `code`.
 *
 * Each loader fetches the resource once per call; SearchableSelect caches
 * the resolved list for the lifetime of the open dropdown.
 */

interface AnyRow extends Record<string, unknown> {
  id?: number | string;
  code?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

function pickList(d: unknown): AnyRow[] {
  if (Array.isArray(d)) return d as AnyRow[];
  if (d && typeof d === 'object') {
    const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(o.items)) return o.items as AnyRow[];
    if (Array.isArray(o.data)) return o.data as AnyRow[];
    if (o.data && Array.isArray((o.data as { items?: unknown[] }).items)) {
      return (o.data as { items: AnyRow[] }).items;
    }
  }
  return [];
}

function labelOf(r: AnyRow): string {
  if (r.name) return String(r.name);
  const combo = [r.firstName, r.lastName].filter(Boolean).join(' ').trim();
  if (combo) return combo;
  if (r.code) return String(r.code);
  return `#${r.id ?? '?'}`;
}

/**
 * Build a loader for a given API resource path. The option value is the
 * numeric id (FK target); rows without any id are dropped.
 */
export function makeFkLoader(path: string): () => Promise<SearchableSelectOption[]> {
  return async () => {
    try {
      const r = await apiClient.get(path);
      return pickList(r.data)
        .map((row) => ({
          value: String(row.id ?? row.code ?? ''),
          label: labelOf(row),
        }))
        .filter((o) => o.value);
    } catch {
      return [];
    }
  };
}

export const loadCustomerOptions = makeFkLoader('/crm/Customer');
export const loadSupplierOptions = makeFkLoader('/purchase/Supplier');
export const loadProductOptions = makeFkLoader('/master-data/Product');
export const loadProductCategoryOptions = makeFkLoader('/master-data/ProductCategory');
export const loadBranchOptions = makeFkLoader('/tenancy/Branch');
export const loadEmployeeOptions = makeFkLoader('/hrm/Employee');
export const loadDepartmentOptions = makeFkLoader('/master-data/Department');
export const loadSalesOrderOptions = makeFkLoader('/sales/SalesOrder');
export const loadSalesInvoiceOptions = makeFkLoader('/sales/SalesInvoice');
export const loadLeaveTypeOptions = makeFkLoader('/hrm/LeaveType');
export const loadMenuCategoryOptions = makeFkLoader('/restaurant/MenuCategory');
